import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Plus, FileText, LogOut, ChevronRight, Search } from "lucide-react";
import { useAdmin } from "@/admin/AdminContext";
import { api } from "@/admin/api";
import { STATUS, fmtPrice } from "@/data/devisCatalog";

const fmtDate = (iso) => {
    if (!iso) return "";
    const [y, m, d] = iso.slice(0, 10).split("-");
    return `${d}/${m}/${y}`;
};

const AdminDashboard = () => {
    const { admin, logout } = useAdmin();
    const navigate = useNavigate();
    const [devis, setDevis] = useState([]);
    const [query, setQuery] = useState("");
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");

    useEffect(() => {
        if (admin === null) {
            navigate("/admin", { replace: true });
            return;
        }
        if (!admin) return;
        api.listDevis()
            .then(setDevis)
            .catch((e) => setError(e.message))
            .finally(() => setLoading(false));
    }, [admin, navigate]);

    if (!admin) return null;

    const norm = (s) => (s || "").toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "");
    const q = norm(query.trim());
    const filtered = q
        ? devis.filter((d) =>
            [d.number, d.invoice_number, d.client?.name, d.client?.company, d.client?.city, d.client?.phone]
                .some((field) => norm(field).includes(q)))
        : devis;

    const counts = {
        brouillon: devis.filter((d) => d.status === "brouillon").length,
        signe: devis.filter((d) => d.status === "signe").length,
        facture: devis.filter((d) => d.status === "facture").length,
    };

    return (
        <div data-testid="admin-dashboard" className="min-h-screen bg-mist pb-28">
            <header className="sticky top-0 z-40 border-b border-black/10 bg-white">
                <div className="mx-auto flex max-w-3xl items-center justify-between px-5 py-3">
                    <Link to="/admin/dashboard" className="flex items-center gap-3">
                        <img src="/assets/logo.png" alt="Eradikbreizh" className="h-9 w-auto" />
                        <span className="rounded-full bg-brand/10 px-3 py-1 text-[10px] font-bold uppercase tracking-[0.2em] text-brand">Admin</span>
                    </Link>
                    <button
                        data-testid="admin-logout-button"
                        onClick={logout}
                        className="inline-flex items-center gap-1.5 rounded-full border border-black/15 px-4 py-2 text-sm font-semibold text-slate-600 transition-colors hover:border-red-300 hover:text-red-600"
                    >
                        <LogOut className="h-4 w-4" />
                        Sortir
                    </button>
                </div>
            </header>

            <main className="mx-auto max-w-3xl px-5 pt-6">
                <div className="grid grid-cols-3 gap-3">
                    <div className="rounded-2xl border border-black/10 bg-white p-4 text-center">
                        <p className="font-display text-2xl font-black text-amber-600">{counts.brouillon}</p>
                        <p className="mt-1 text-[11px] font-bold uppercase tracking-wide text-slate-500">À signer</p>
                    </div>
                    <div className="rounded-2xl border border-black/10 bg-white p-4 text-center">
                        <p className="font-display text-2xl font-black text-blue-700">{counts.signe}</p>
                        <p className="mt-1 text-[11px] font-bold uppercase tracking-wide text-slate-500">En cours</p>
                    </div>
                    <div className="rounded-2xl border border-black/10 bg-white p-4 text-center">
                        <p className="font-display text-2xl font-black text-green-700">{counts.facture}</p>
                        <p className="mt-1 text-[11px] font-bold uppercase tracking-wide text-slate-500">Facturés</p>
                    </div>
                </div>

                <h1 className="mt-8 font-display text-xl font-black uppercase tracking-tight text-night">Devis &amp; factures</h1>

                <div className="relative mt-4">
                    <Search className="pointer-events-none absolute left-4 top-1/2 h-5 w-5 -translate-y-1/2 text-slate-400" />
                    <input
                        data-testid="admin-search-input"
                        type="search"
                        value={query}
                        onChange={(e) => setQuery(e.target.value)}
                        placeholder="Rechercher : nom, société, ville, numéro..."
                        className="w-full rounded-full border border-black/15 bg-white py-4 pl-12 pr-5 text-base outline-none transition-colors focus:border-brand"
                    />
                </div>

                {loading && <p className="mt-6 text-sm text-slate-500">Chargement...</p>}
                {error && <p data-testid="admin-dashboard-error" className="mt-6 rounded-2xl bg-red-50 px-4 py-3 text-sm font-semibold text-red-700">{error}</p>}
                {!loading && !error && devis.length === 0 && (
                    <div className="mt-6 rounded-3xl border border-dashed border-black/15 bg-white p-8 text-center">
                        <FileText className="mx-auto h-10 w-10 text-slate-300" />
                        <p className="mt-3 text-sm text-slate-500">Aucun devis pour le moment.<br />Créez votre premier devis D-650 chez le client.</p>
                    </div>
                )}
                {!loading && !error && devis.length > 0 && filtered.length === 0 && (
                    <p data-testid="admin-search-empty" className="mt-6 rounded-2xl border border-dashed border-black/15 bg-white px-5 py-6 text-center text-sm text-slate-500">
                        Aucun devis ne correspond à « {query} ».
                    </p>
                )}

                <ul className="mt-4 space-y-3">
                    {filtered.map((d) => {
                        const st = STATUS[d.status] || STATUS.brouillon;
                        return (
                            <li key={d.id}>
                                <button
                                    data-testid={`admin-devis-card-${d.seq}`}
                                    onClick={() => navigate(`/admin/devis/${d.id}`)}
                                    className="flex w-full items-center gap-4 rounded-2xl border border-black/10 bg-white p-4 text-left shadow-sm transition-[border-color,transform] duration-200 hover:border-brand active:scale-[0.99]"
                                >
                                    <div className="min-w-0 flex-1">
                                        <div className="flex items-center gap-2">
                                            <span className="font-display text-base font-black text-night">{d.number}</span>
                                            <span className={`rounded-full px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-wide ${st.classes}`}>{st.label}</span>
                                        </div>
                                        <p className="mt-1 truncate text-sm font-semibold text-slate-700">{d.client?.name} {d.client?.city ? `· ${d.client.city}` : ""}</p>
                                        <p className="mt-0.5 text-xs text-slate-500">
                                            Intervention le {fmtDate(d.intervention_date)}
                                            {d.surcharge_applied && " · +20 %"}
                                        </p>
                                    </div>
                                    <div className="text-right">
                                        <p className="font-display text-lg font-black text-brand">{fmtPrice(d.total_ht)}</p>
                                        <ChevronRight className="ml-auto mt-1 h-4 w-4 text-slate-400" />
                                    </div>
                                </button>
                            </li>
                        );
                    })}
                </ul>
            </main>

            <div className="fixed inset-x-0 bottom-0 z-40 border-t border-black/10 bg-white/95 p-4 backdrop-blur">
                <div className="mx-auto max-w-3xl">
                    <Link
                        data-testid="admin-new-devis-button"
                        to="/admin/devis/nouveau"
                        className="flex w-full items-center justify-center gap-2 rounded-full bg-brand py-4 text-base font-bold text-white transition-[background-color,transform] duration-300 hover:bg-brand-hover active:scale-95"
                    >
                        <Plus className="h-5 w-5" />
                        Nouveau devis
                    </Link>
                </div>
            </div>
        </div>
    );
};

export default AdminDashboard;
